import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SkillData, useApiClient } from '@/lib/api';
import { Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface SkillsViewProps {
    employmentId?: string;
    educationId?: string;
    profileSectionId?: string;
    skills?: SkillData[];
}

const SkillsView: React.FC<SkillsViewProps> = ({
    employmentId,
    educationId,
    profileSectionId,
    skills: initialSkills,
}) => {

    const { api } = useApiClient();
    const [newSkill, setNewSkill] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(initialSkills === undefined);
    const [skills, setSkills] = useState<SkillData[]>(initialSkills || []);

    useEffect(() => {
        if (initialSkills !== undefined) {
            setSkills(initialSkills);
            setIsInitialLoading(false);
            return;
        }

        const fetchSkills = async () => {
            setIsInitialLoading(true);
            try {
                let fetchedSkills: SkillData[] = [];
                if (employmentId) {
                    fetchedSkills = await api.getSkillsByEmploymentId(employmentId);
                } else if (educationId) {
                    fetchedSkills = await api.getSkillsByEducationId(educationId);
                } else if (profileSectionId) {
                    fetchedSkills = await api.getSkillsByProfileSectionId(profileSectionId);
                } else {
                    const allSkills = await api.getSkills();
                    fetchedSkills = allSkills.filter(
                        skill => !skill.employment_id && !skill.education_id && !skill.profile_section_id
                    );
                }
                setSkills(fetchedSkills);
            } catch (error) {
                console.error('Failed to fetch skills', error);
            } finally {
                setIsInitialLoading(false);
            }
        };

        fetchSkills();
    }, [api, employmentId, educationId, profileSectionId, initialSkills]);

    const handleAddSkill = async () => {
        if (!newSkill.trim()) return;

        setIsLoading(true);
        try {
            const skillToAdd = {
                name: newSkill,
                employment_id: employmentId,
                education_id: educationId,
                profile_section_id: profileSectionId,
            };
            const createdSkill = await api.createSkill(skillToAdd);

            setSkills(prevSkills => [...prevSkills, {
                id: createdSkill.id,
                name: createdSkill.name,
            }]);
            setNewSkill('');
        } catch (error) {
            console.error('Failed to add skill', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteSkill = async (id: string) => {
        setIsLoading(true);
        try {
            await api.deleteSkill(id);
            setSkills(prevSkills => prevSkills.filter(skill => skill.id !== id));
        } catch (error) {
            console.error('Failed to delete skill', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (isInitialLoading) {
        return (
            <div className="space-y-4 animate-pulse">
                <div className="flex flex-wrap gap-2">
                    <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                    <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                    <div className="h-6 bg-gray-200 rounded-full w-14"></div>
                </div>
                <div className="h-10 bg-gray-100 rounded"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div>
                {skills.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {skills.map(skill => (
                            <Badge
                                key={skill.id}
                                className="px-3 py-1 flex items-center gap-2"
                                variant="secondary"
                            >
                                {skill.name}
                                <button
                                    onClick={() => handleDeleteSkill(skill.id!)}
                                    className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-red-600 transition hover:bg-red-200"
                                    disabled={isLoading}
                                    aria-label={`Delete ${skill.name}`}
                                >
                                    <Trash2 className="h-3 w-3" />
                                </button>
                            </Badge>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500 italic">No skills added yet.</p>
                )}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                <div className="flex-1">
                    <Input
                        value={newSkill}
                        onChange={e => setNewSkill(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !isLoading && newSkill.trim()) {
                                e.preventDefault();
                                handleAddSkill();
                            }
                        }}
                        placeholder="e.g., Scheduling, Microsoft Office, customer service..."
                        disabled={isLoading}
                        maxLength={255}
                    />
                </div>
                <Button
                    variant="default"
                    size="icon"
                    onClick={handleAddSkill}
                    disabled={isLoading || !newSkill.trim()}
                    className="h-10 w-10 rounded-full bg-interview-blue text-white hover:bg-interview-blue/90"
                    aria-label={isLoading ? 'Adding skill' : 'Add skill'}
                >
                    <Plus className="h-5 w-5" />
                </Button>
            </div>
        </div>
    );
};

export default SkillsView;
